import { useState } from "react";
import { compactInputClass, mfgCompactGhostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { PriceChangeNarrowFilters, PriceChangePricePresence } from "@/lib/manufacturing/priceChangeSnapshot";

const fieldClass = `${compactInputClass} text-xs`;

const KIND_CHOICES = [
  { value: "product", label: "Product" },
  { value: "option", label: "Option" },
  { value: "package", label: "Package" },
  { value: "variant", label: "Variant" },
] as const;

const PRESENCE_FIELDS: { key: keyof PriceChangeNarrowFilters; label: string }[] = [
  { key: "newWholesale", label: "New WS" },
  { key: "newMsrp", label: "New MSRP" },
  { key: "currentWholesale", label: "WS now" },
  { key: "currentMsrp", label: "MSRP now" },
  { key: "cogsWholesale", label: "WS COGS" },
  { key: "cogsMsrp", label: "MSRP COGS" },
];

function kindChoices(present: readonly string[]): { value: string; label: string }[] {
  const known = new Set<string>(KIND_CHOICES.map((choice) => choice.value));
  const extra = present
    .map((kind) => kind.trim())
    .filter((kind) => kind && !known.has(kind.toLowerCase()))
    .map((kind) => ({ value: kind.toLowerCase(), label: kind }));
  return [...KIND_CHOICES, ...extra];
}

function PresenceSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: PriceChangePricePresence;
  onChange: (value: PriceChangePricePresence) => void;
}) {
  return (
    <select className={`${fieldClass} w-40`} value={value} aria-label={label} onChange={(event) => onChange(event.target.value as PriceChangePricePresence)}>
      <option value="">{label}</option>
      <option value="blank">{label} blank</option>
      <option value="filled">{label} filled</option>
    </select>
  );
}

export function ManufacturingModelsPriceChangeFilters({
  search,
  setSearch,
  group,
  setGroup,
  groups,
  roof,
  setRoof,
  roofs,
  kinds,
  narrow,
  setNarrowField,
}: {
  search: string;
  setSearch: (value: string) => void;
  group: string;
  setGroup: (value: string) => void;
  groups: readonly string[];
  roof: string;
  setRoof: (value: string) => void;
  roofs: readonly string[];
  kinds: readonly string[];
  narrow: PriceChangeNarrowFilters;
  setNarrowField: (key: keyof PriceChangeNarrowFilters, value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const active = Object.values(narrow).filter((value) => value).length;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          className={`${fieldClass} w-44`}
          value={search}
          placeholder="Search code or name"
          onChange={(event) => setSearch(event.target.value)}
        />
        <select className={`${fieldClass} w-40`} value={group} aria-label="Group" onChange={(event) => setGroup(event.target.value)}>
          <option value="">All groups</option>
          {groups.map((label) => (
            <option key={label} value={label}>
              {label}
            </option>
          ))}
        </select>
        <select className={`${fieldClass} w-40`} value={roof} aria-label="Style" onChange={(event) => setRoof(event.target.value)}>
          <option value="">All styles</option>
          {roofs.map((label) => (
            <option key={label} value={label}>
              {label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className={mfgCompactGhostBtnClass}
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? "Hide narrow filters" : "Narrow filters"}
          {active ? ` (${active})` : ""}
        </button>
      </div>
      {open ? (
        <div className="space-y-2 rounded-xl border border-dashed border-gray-300 p-2 dark:border-surface-border-strong">
          <p className="text-xs text-gray-600 dark:text-surface-muted">
            These sit on top of group and style. Use them to focus a pass, such as products whose new wholesale is still blank.
          </p>
          <div className="flex flex-wrap gap-2">
            <select
              className={`${fieldClass} w-40`}
              value={narrow.kind}
              aria-label="Kind"
              onChange={(event) => setNarrowField("kind", event.target.value)}
            >
              <option value="">Kind</option>
              {kindChoices(kinds).map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
            {PRESENCE_FIELDS.map((field) => (
              <PresenceSelect
                key={field.key}
                label={field.label}
                value={narrow[field.key] as PriceChangePricePresence}
                onChange={(value) => setNarrowField(field.key, value)}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
